import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import React, { useEffect, useMemo, useState } from 'react';
import { Chart } from 'react-chartjs-2';
import { FormatToShortPriceTemplate } from '../../../../utils/FormatValue';
import {
  MonthYearValue,
  findHighestValueInDictionary,
  flattenDataByYear,
  groupDataByYear,
} from '../../../../utils/MonthYear/MonthYearValue';
import { months, monthsFullWord } from '../../../../utils/MonthYear/monthYear';
import { ChevronButton } from '../../Buttons/ChevronButton';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

interface Props {
  barChartData?: MonthYearValue[];
  barChartTitle: string;
  graphChartData?: MonthYearValue[];
  graphChartTitle: string;
  sumValueOfEditableProperty: number;
  updateEditablePropertyFunc?: (data: MonthYearValue[]) => void;
  chartTypeToEdit?: 'bar' | 'graph';
  enableNegativeValues?: boolean;
  singleYearView?: boolean;
  graphColor?: string; // In #Code
  barColor?: string; // In #Code
  showAllMonths?: boolean;
  changeGraphToBar?: boolean;
  animationTime?: number;
  isHideValueInput?: boolean;
}

const emptyData: MonthYearValue[] = [
  { date: { month: 1, year: 2024 }, value: 0 },
  { date: { month: 2, year: 2024 }, value: 0 },
  { date: { month: 3, year: 2024 }, value: 0 },
  { date: { month: 4, year: 2024 }, value: 0 },
  { date: { month: 5, year: 2024 }, value: 0 },
  { date: { month: 6, year: 2024 }, value: 0 },
  { date: { month: 7, year: 2024 }, value: 0 },
  { date: { month: 8, year: 2024 }, value: 0 },
  { date: { month: 9, year: 2024 }, value: 0 },
  { date: { month: 10, year: 2024 }, value: 0 },
  { date: { month: 11, year: 2024 }, value: 0 },
  { date: { month: 12, year: 2024 }, value: 0 },
];

const MonthYearValueBarGraphChart: React.FC<Props> = ({
  barChartData,
  graphChartData,
  barChartTitle,
  sumValueOfEditableProperty,
  graphChartTitle,
  updateEditablePropertyFunc,
  barColor = '#F3A1B8',
  graphColor = '#5467B2',
  singleYearView = true,
  enableNegativeValues = false,
  showAllMonths = true,
  changeGraphToBar = false,
  animationTime = 1000,
  isHideValueInput = false,
}) => {
  const [barChartDataByYear, setBarChartDataByYear] = useState(() => groupDataByYear(barChartData || emptyData) || {});
  const [graphChartDataByYear, setGraphChartDataByYear] = useState(
    () => groupDataByYear(graphChartData || emptyData) || {},
  );
  const [isChangeableValueStable, setIsChangeableValueStable] = useState(true);
  const [currentValue, setCurrentValue] = useState(sumValueOfEditableProperty);
  const [currentYearIndex, setCurrentYearIndex] = useState(0); // Current Year Being

  const barMaxValue = useMemo(() => {
    return findHighestValueInDictionary(barChartDataByYear) * 1.2;
  }, [barChartDataByYear]);

  const graphMaxValue = useMemo(() => {
    return findHighestValueInDictionary(graphChartDataByYear) * 1.2;
  }, [graphChartDataByYear]);

  useEffect(() => {
    currentValue != sumValueOfEditableProperty ? setIsChangeableValueStable(false) : setIsChangeableValueStable(true);
  }, [currentValue]);

  useEffect(() => {
    setBarChartDataByYear(groupDataByYear(barChartData ? barChartData : emptyData));
    setGraphChartDataByYear(groupDataByYear(graphChartData ? graphChartData : emptyData));
    setCurrentValue(sumValueOfEditableProperty);
  }, [graphChartData, barChartData]);

  //This section Updates the main state - should update only if the data is OK
  useEffect(() => {
    if (isChangeableValueStable && updateEditablePropertyFunc) {
      updateEditablePropertyFunc(flattenDataByYear(graphChartDataByYear));
      updateEditablePropertyFunc(flattenDataByYear(barChartDataByYear));
    }
  }, [isChangeableValueStable]);

  const CustomLegend = () => {
    return (
      <div className="flex ">
        {graphChartTitle && (
          <>
            <div className={`w-[1.2rem] h-[0.2rem] my-auto mx-[0.5rem]`} style={{ background: graphColor }}></div>
            <div className="text-base">{graphChartTitle}</div>
          </>
        )}
        <div
          className={`w-[1.2rem] h-[0.2rem] my-auto ms-[1.25rem] me-[0.5rem]`}
          style={{ background: barColor }}
        ></div>
        <div className="text-base">{barChartTitle}</div>
      </div>
    );
  };

  const InvalidValueErrorMessage = () => {
    return (
      <>
        <div className="text-red text-sm ">{"Input value doesn't match original value "}</div>
        <div className="text-red text-sm border-l-red ">
          {currentValue < sumValueOfEditableProperty ? 'Add' : 'Remove'}
          {+Math.abs(currentValue - sumValueOfEditableProperty)} {"or changes wouldn't be saved"}
        </div>
        <div className="text-red text-sm "></div>
      </>
    );
  };

  const SwitchDisplayedYear = () => {
    return (
      <div className="flex justify-center  ">
        <ChevronButton
          direction="left"
          disabled={currentYearIndex == 0}
          onButtonClick={() => setCurrentYearIndex(currentYearIndex - 1)}
        />
        <div className="text-base mb-0  flex flex-col justify-center">
          {Object.keys(barChartDataByYear)[currentYearIndex]}
        </div>
        <ChevronButton
          direction="right"
          disabled={currentYearIndex == charts.length - 1}
          onButtonClick={() => setCurrentYearIndex(currentYearIndex + 1)}
        />
      </div>
    );
  };

  const charts = Object.keys(barChartDataByYear).map((year, index) => {
    const barYearData = barChartDataByYear[parseInt(year)];
    const graphYearData = graphChartDataByYear[parseInt(year)];

    const specifiedMonths = showAllMonths
      ? Array.from({ length: 12 }, (_, i) => i + 1)
      : barYearData.map((d) => d.date.month);

    const chartDataToValueArray = (data: MonthYearValue[]): (number | undefined)[] | undefined => {
      if (!data) return undefined;
      return specifiedMonths.map((month) => {
        const monthData = data.find((d) => d.date.month === month);
        return monthData ? monthData.value : undefined;
      });
    };

    const options = {
      animation: {
        duration: animationTime, // Disable animation
      },
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
          position: 'top' as const,
        },
        title: {
          display: false,
          text: `Combined Bar and Line Chart for ${year}`,
        },
      },
      scales: {
        y1: {
          type: 'linear' as const,
          display: true,
          position: 'left' as const,
          beginAtZero: true,
          title: {
            display: true,
            text: barChartTitle,
          },

          suggestedMax: barMaxValue,
          ticks: {
            callback: function (value: any) {
              return value;
            },
          },
        },
        y2: {
          type: 'linear' as const,
          display: true,
          position: 'right' as const,
          beginAtZero: true,
          title: {
            display: true,
            text: graphChartTitle,
          },

          suggestedMax: graphMaxValue,
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            callback: function (value: any) {
              // Directly using the function to format values here
              return FormatToShortPriceTemplate(value, '');
            },
          },
        },
      },
    };

    const chartData = {
      labels: specifiedMonths.map((d) => months[d - 1]),
      datasets: [
        {
          type: 'bar' as const,
          label: barChartTitle,
          data: chartDataToValueArray(barYearData),
          borderWidth: 2,
          borderColor: barColor,
          backgroundColor: 'transparent',
          yAxisID: 'y1',
          order: 1,
          maxBarThickness: 20, // Adjust this value as needed
          categoryPercentage: 0.8, // Adjust this value to increase/decrease the space between bars
          barPercentage: 0.8, // Adjust this value to increase/decrease the bar width
        },
        {
          type: changeGraphToBar ? ('bar' as const) : ('line' as const),
          label: graphChartTitle,
          data: chartDataToValueArray(graphYearData),
          borderColor: graphColor,
          backgroundColor: graphColor,
          fill: false,
          yAxisID: 'y2',
          order: 1,
          maxBarThickness: 20, // Adjust this value as needed
          categoryPercentage: 0.8, // Adjust this value to increase/decrease the space between bars
          barPercentage: 0.8, // Adjust this value to increase/decrease the bar width
        },
      ],
    };

    const handleInputChangeBar = (year: number, index: number, newValue: number) => {
      if (newValue < 0 && !enableNegativeValues) {
        return;
      }
      const updatedDataByBarYear = { ...barChartDataByYear };
      const fixIndex = index - updatedDataByBarYear[year][0].date.month + 1;
      setCurrentValue(currentValue - updatedDataByBarYear[year][fixIndex].value + newValue);
      updatedDataByBarYear[year][fixIndex].value = newValue;
      setBarChartDataByYear(updatedDataByBarYear);
    };

    return (
      <div key={'chart- ' + index} className="flex flex-1 grow  h-[16rem]">
        <div className="flex flex-1 space-x-[1rem]    ">
          {!singleYearView && <div className="my-auto ">{year}</div>}
          <div className="flex flex-1  flex-col   " key={year}>
            <div className="flex flex-1  ">
              <Chart className="" type="bar" options={options} data={chartData} />
            </div>
            {updateEditablePropertyFunc && !isHideValueInput && (
              <div className="flex  ps-[5vh] pe-[5vh] ">
                <div className="flex flex-1  justify-around  ">
                  {chartDataToValueArray(barYearData)?.map((data, index) => (
                    <input
                      disabled={!data}
                      key={`bar-${year}-${index}`}
                      title={`Edit ${monthsFullWord[index]} `}
                      type="number"
                      value={data || 0}
                      onChange={(e) => {
                        e.target.value && handleInputChangeBar(parseInt(year), index, parseInt(e.target.value));
                      }}
                      className={`min-w-[3rem] max-w-[4rem] w-full h-[2rem]  
                            hide-spin-sm-md show-spin-lg-xl 
                             border-1 border-${
                               isChangeableValueStable ? 'gray-300' : 'red'
                             } border-opacity-50 text-center rounded ${!data && 'text-gray-450'} `}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  });

  return (
    <div className=" flex flex-col flex-1    ">
      {singleYearView ? (
        <div className="  ">
          <SwitchDisplayedYear />
          {charts[currentYearIndex]}
        </div>
      ) : (
        <div className="">{charts}</div>
      )}
      {!isChangeableValueStable && <InvalidValueErrorMessage />}
      <div className=" flex justify-center mt-[1rem] ">
        <CustomLegend />
      </div>
    </div>
  );
};

export default MonthYearValueBarGraphChart;
